import QRCode from 'qrcode.react';
import React, { useContext, useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import * as R from 'ramda';

import Button, { ButtonStyle } from '../../../common/components/Button/Button';
import ViewContainer from '../../../common/components/Container/Container';
import Loading from '../../../common/components/Loading/Loading';
import TitleContainer from '../../../common/components/TitleContainer/TitleContainer';
import SVGCaretLeft from '../../../common/assets/svg/icons/SVGCaretLeft';
import SVGCopy from '../../../common/assets/svg/icons/SVGCopy';
import { Routes } from '../../../config/routes';
import FlowMode from '../../../providers/validation/FlowMode';
import { ValidationContext } from '../../../providers/ValidationContext';
import { FlowConfigContext } from '../../../providers/FlowConfigContext';
import { ExchangeTokenFetchStatus, LinkDevicesContext } from '../../../providers/LinkDevicesContext';
import { ExchangeTokenStatus } from '../../../services/authentication';
import withRouteAnalytics from '../../../services/analytics/withRouteAnalytics';
import { Screens } from '../../../services/analytics/screens';
import ButtonContainer from '../../../common/components/Button/ButtonContainer';

function ContinueWithLinkScreen(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { initialized } = useContext(FlowConfigContext);
  const { setFlowMode } = useContext(ValidationContext);
  const { generateLink, exchangeTokenStatus, clearCurrentLink } = useContext(LinkDevicesContext);
  const [link, setLink] = useState<string | null>(null);
  const [breakoutLink, setBreakoutLink] = useState<string | undefined>(undefined);
  const [linkExpired, setLinkExpired] = useState<boolean>(false);
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const { enableBreakoutFlow } = location.state || {};

  useEffect(() => {
    setLinkExpired(R.contains(exchangeTokenStatus, [ExchangeTokenFetchStatus.failedRetry]));
  }, [exchangeTokenStatus]);

  useEffect(() => {
    if (!linkCopied) {
      return;
    }

    // reset link copied status if we are fetching a new token or if the token is in a failed status
    if (R.contains(exchangeTokenStatus, [ExchangeTokenFetchStatus.fetching, ExchangeTokenFetchStatus.failedRetry])) {
      setLinkCopied(false);
    }
  }, [exchangeTokenStatus]);

  const generate = async () => {
    const generateLinkResult = await generateLink();
    if (generateLinkResult.isFailed()) {
      return;
    }
    const link = generateLinkResult.success();
    setLink(link);

    // Encode the URL to open it in the browser.
    // This is because a webview callback is required to intercept the link clicking and divert it to the native browser.
    // This is, however, loading the link before it's opened in the browser, which invalidates the one-time use token.
    // So we wrap the link in a Civic redirect URL so the webview callback can extract it from there and open in the browser without loading it first.
    const encodedLink = encodeURIComponent(link);
    setBreakoutLink(`hl://redirect/${encodedLink}`);
  };

  const handleBackButtonClick = () => {
    // When navigating back we need to reset the flow mode so the user can choose again
    setFlowMode(FlowMode.userSelect);
    clearCurrentLink();
    const backRoute = location.state?.from;
    navigate(backRoute ?? -1);
  };

  useEffect(() => {
    if (!initialized) {
      return;
    }

    generate();
  }, [initialized]);

  // Monitor exchange token status and navigate to devices linked screen on activated
  // if devices are linked, re-add the previous step since it will be processed oh the other device
  useEffect(() => {
    if (exchangeTokenStatus === ExchangeTokenStatus.activated) {
      navigate(Routes.DEVICE_LINKED);
    }
  }, [exchangeTokenStatus]);

  return (
    <ViewContainer verticalPadding="py-6">
      <div className="flex-basis-0 items-left w-full">
        <Button
          style={ButtonStyle.BACK}
          onClick={handleBackButtonClick}
          disabled={exchangeTokenStatus === ExchangeTokenFetchStatus.fetching}
          extraClasses="back-button"
          dataTestid="BACK_BUTTON"
        >
          <div className="flex text-textTertiary uppercase text-xs items-center">
            <SVGCaretLeft />
            <span className="ml-2">{t('common.explainer.backButtonLabel')}</span>
          </div>
        </Button>
      </div>
      <div className="mt-4">
        {enableBreakoutFlow && (
          <TitleContainer center>
            <h1 data-testid="CONTINUE_WITH_LINK">{t('screens.documentPass.continueWithBreakoutLink.title')}</h1>
            <div className="text-xl leading-6 mt-4 text-textParagraph" data-testid="CONTINUE_WITH_LINK_DESCRIPTION">
              <p>{t('screens.documentPass.continueWithBreakoutLink.description')}</p>
            </div>
          </TitleContainer>
        )}
        {!enableBreakoutFlow && (
          <TitleContainer center>
            <h1 data-testid="CONTINUE_WITH_LINK">{t('screens.documentPass.continueWithLink.title')}</h1>
            <div className="text-xl leading-6 mt-4 text-textParagraph" data-testid="CONTINUE_WITH_LINK_DESCRIPTION">
              <p>{t('screens.documentPass.continueWithLink.description')}</p>
            </div>
          </TitleContainer>
        )}
      </div>
      <>
        {exchangeTokenStatus === ExchangeTokenFetchStatus.fetching && (
          <div className="my-4 flex flex-col justify-center bg-transparent">
            <Loading data-testid="CONTINUE_WITH_LINK_LOADING" />
          </div>
        )}
        {exchangeTokenStatus === ExchangeTokenStatus.pending && link && enableBreakoutFlow && (
          <a
            className="text-primary border border-primary rounded-full px-4 py-3 font-bold hover:bg-primary hover:bg-opacity-10"
            href={breakoutLink}
            target="_blank"
            rel="noreferrer"
          >
            {t('screens.documentPass.continueWithBreakoutLink.continueButton')}
          </a>
        )}
        {exchangeTokenStatus === ExchangeTokenStatus.pending && link && !enableBreakoutFlow && (
          <QRCode
            data-testid="CONTINUE_WITH_LINK_QR_CODE"
            className="my-4 border-8 border-white"
            value={link}
            fgColor="#7C75FF"
            size={256}
          />
        )}

        {exchangeTokenStatus === ExchangeTokenFetchStatus.failedRetry && !enableBreakoutFlow && (
          <div className="relative my-4">
            <QRCode
              data-testid="CONTINUE_WITH_LINK_QR_CODE"
              className="border-8 border-transparent/[.15]"
              value="https://www.civic.com"
              fgColor="#7C75FF33"
              bgColor="#BBBBBB"
              size={256}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <Button
                style={ButtonStyle.DEFAULT}
                extraClasses="mt-2"
                disabled={false}
                onClick={generate}
                dataTestid="CONTINUE_WITH_LINK_RETRY"
              >
                <span>{t('screens.documentPass.continueWithLink.regenerateCode')}</span>
              </Button>
            </div>
          </div>
        )}

        {exchangeTokenStatus === ExchangeTokenFetchStatus.failedRetry && enableBreakoutFlow && (
          <ButtonContainer>
            <Button
              style={ButtonStyle.DEFAULT}
              dataTestid="CHOOSE_CONTINUE_ON_WEB"
              disabled={false} //TODO: This should ideally never happen if we auto-renew the token before it expires.
              onClick={generate}
              loading={R.isNil(link)}
            >
              <div className="flex items-center text-textButton font-normal">
                <span>{t('screens.documentPass.continueWithBreakoutLink.regenerateLink')}</span>
              </div>
            </Button>
          </ButtonContainer>
        )}
      </>
      <>
        {exchangeTokenStatus === ExchangeTokenStatus.pending && link && !enableBreakoutFlow && (
          <>
            <div className="text-xl leading-6 mb-4 text-textHeadline">
              <p>{t('screens.documentPass.continueWithLink.or')}</p>
            </div>
            <div className="full-width-ruler" />
            <CopyToClipboard
              text={link || ''}
              onCopy={() => setLinkCopied(exchangeTokenStatus === ExchangeTokenStatus.pending && link !== null)}
            >
              <Button
                style={ButtonStyle.WIDE_SELECTOR}
                onClick={() => {}} /* click is handled by parent CopyToClipboard element */
                disabled={false}
                dataTestid="CONTINUE_WITH_LINK_COPY_LINK"
              >
                <div className="py-4 px-8 grid grid-cols-5 w-full items-center justify-left">
                  <span className="col-span-4 text-left">{t('screens.documentPass.continueWithLink.copyLink')}</span>
                  <div>
                    <span className="float-right">
                      <SVGCopy />
                    </span>
                  </div>
                </div>
              </Button>
            </CopyToClipboard>
            <div className="full-width-ruler mb-4" />
            {linkCopied && (
              <div
                className="text-lg leading-6 text-textParagraph flex justify-center w-full"
                data-testid="LINK_COPIED_MESSAGE"
              >
                {t('screens.documentPass.continueWithLink.linkCopied')}
              </div>
            )}
          </>
        )}
      </>
    </ViewContainer>
  );
}
export default withRouteAnalytics(ContinueWithLinkScreen, Screens.DOCUMENT_MOBILE_HANDOFF_QR);
